/**
 * Configuracion de Avisos (Configuracion -> Avisos). Solo admin.
 * Por cada tipo de evento: activar/desactivar y elegir destinatarios
 * (roles globales, roles del estudio y usuarios concretos).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Bell, FloppyDisk, Users, Buildings, UserCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationRulesPanel({ lang }) {
  const es = lang === 'es';
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const EMPTY_MANUAL = { title: '', body: '', link: '', global_roles: [], user_ids: [] };
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [sending, setSending] = useState(false);

  const L = {
    title: es ? 'Configuración de Avisos' : 'Alerts configuration',
    intro: es
      ? 'Activa cada aviso y elige a quién debe llegar (por rol global, por rol dentro del estudio o usuarios concretos).'
      : 'Enable each alert and choose recipients (global role, study-team role or specific users).',
    active: es ? 'Activo' : 'Active',
    globalRoles: es ? 'Roles globales' : 'Global roles',
    studyRoles: es ? 'Roles del estudio' : 'Study-team roles',
    users: es ? 'Usuarios concretos' : 'Specific users',
    save: es ? 'Guardar' : 'Save',
    saved: es ? 'Aviso guardado' : 'Alert saved',
    none: es ? 'Ninguno' : 'None',
    select: es ? 'Seleccionar' : 'Select',
    studyScope: es ? 'Evento de estudio' : 'Study event',
    manualTitle: es ? 'Enviar aviso manual' : 'Send manual alert',
    manualIntro: es ? 'Redacta un aviso y envíalo a los roles o usuarios que elijas.' : 'Write an alert and send it to the roles or users you choose.',
    mTitle: es ? 'Título' : 'Title',
    mBody: es ? 'Mensaje' : 'Message',
    mLink: es ? 'Enlace (opcional)' : 'Link (optional)',
    send: es ? 'Enviar aviso' : 'Send alert',
    sent: es ? 'Aviso enviado a {n} usuario(s)' : 'Alert sent to {n} user(s)',
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/api/config/notification-rules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
      ]);
      setEvents(r1.data?.events || []);
      setRoles(r1.data?.roles || []);
      setUsers((r2.data || []).map((u) => ({ id: u.id, name: u.name || u.email, email: u.email })));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = (idx, field, value) =>
    setEvents((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  const toggleIn = (idx, field, key) => {
    setEvents((prev) => prev.map((e, i) => {
      if (i !== idx) return e;
      const arr = new Set(e[field] || []);
      if (arr.has(key)) arr.delete(key); else arr.add(key);
      return { ...e, [field]: Array.from(arr) };
    }));
  };

  const save = async (ev) => {
    setSavingId(ev.event_type);
    try {
      await axios.put(`${API_URL}/api/config/notification-rules/${ev.event_type}`, {
        enabled: ev.enabled,
        global_roles: ev.global_roles || [],
        study_roles: ev.study_roles || [],
        user_ids: ev.user_ids || [],
      }, { withCredentials: true });
      toast.success(L.saved);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSavingId(null);
    }
  };

  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || key;
  const userLabel = (id) => users.find((u) => u.id === id)?.name || id;

  const toggleManual = (field, key) => {
    setManual((m) => {
      const arr = new Set(m[field] || []);
      if (arr.has(key)) arr.delete(key); else arr.add(key);
      return { ...m, [field]: Array.from(arr) };
    });
  };

  const sendManual = async () => {
    if (!manual.title.trim()) { toast.error(es ? 'El título es obligatorio' : 'Title is required'); return; }
    if (!(manual.global_roles.length || manual.user_ids.length)) {
      toast.error(es ? 'Selecciona al menos un destinatario' : 'Select at least one recipient'); return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/config/notifications/manual`, manual, { withCredentials: true });
      toast.success(L.sent.replace('{n}', data?.sent ?? 0));
      setManual(EMPTY_MANUAL);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  const MultiRoles = ({ idx, field, selected }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs justify-start gap-1 border-stone-300"
          data-testid={`notif-${events[idx].event_type}-${field}-trigger`}>
          {L.select}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2 max-h-72 overflow-y-auto">
        {roles.map((r) => (
          <label key={r.key} className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-stone-50 cursor-pointer">
            <Checkbox checked={(selected || []).includes(r.key)} onCheckedChange={() => toggleIn(idx, field, r.key)}
              data-testid={`notif-${events[idx].event_type}-${field}-${r.key}`} />
            <span className="text-sm text-stone-700">{r.label}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );

  const MultiUsers = ({ idx, selected }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs justify-start gap-1 border-stone-300"
          data-testid={`notif-${events[idx].event_type}-users-trigger`}>
          {L.select}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2 max-h-72 overflow-y-auto">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-stone-50 cursor-pointer min-w-0">
            <Checkbox checked={(selected || []).includes(u.id)} onCheckedChange={() => toggleIn(idx, 'user_ids', u.id)} />
            <span className="text-sm text-stone-700 truncate">{u.name} <span className="text-stone-400 text-xs">{u.email}</span></span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );

  const Chips = ({ values, labelFn }) =>
    (values || []).length === 0
      ? <span className="text-xs text-stone-400">{L.none}</span>
      : <span className="flex flex-wrap gap-1">{values.map((v) => (
          <Badge key={v} variant="outline" className="border-[#1f3e80]/30 text-[#1f3e80] text-[11px]">{labelFn(v)}</Badge>
        ))}</span>;

  if (loading) return <p className="text-sm text-stone-400 py-8">...</p>;

  return (
    <div className="space-y-4" data-testid="notification-rules-panel">
      <div className="flex items-center gap-2">
        <Bell size={20} className="text-[#1f3e80]" />
        <div>
          <h3 className="text-lg font-heading font-semibold text-stone-900">{L.title}</h3>
          <p className="text-xs text-stone-500">{L.intro}</p>
        </div>
      </div>

      {/* Aviso manual */}
      <div className="rounded-sm border border-[#1f3e80]/20 bg-[#1f3e80]/[0.03] p-4 space-y-3" data-testid="manual-notify-card">
        <div className="flex items-center gap-2">
          <PaperPlaneTilt size={18} className="text-[#1f3e80]" />
          <div>
            <h4 className="font-semibold text-stone-900">{L.manualTitle}</h4>
            <p className="text-xs text-stone-500">{L.manualIntro}</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs font-medium text-stone-600">{L.mTitle} <span className="text-red-500">*</span></Label>
            <Input value={manual.title} onChange={(e) => setManual((m) => ({ ...m, title: e.target.value }))}
              className="rounded-sm bg-white" data-testid="manual-title" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-stone-600">{L.mLink}</Label>
            <Input value={manual.link} onChange={(e) => setManual((m) => ({ ...m, link: e.target.value }))}
              placeholder="/studies/EST-0001" className="rounded-sm bg-white" data-testid="manual-link" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium text-stone-600">{L.mBody}</Label>
          <Textarea value={manual.body} onChange={(e) => setManual((m) => ({ ...m, body: e.target.value }))}
            rows={2} className="rounded-sm resize-y bg-white" data-testid="manual-body" />
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600">
            <Users size={13} /> {L.globalRoles}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs border-stone-300" data-testid="manual-roles-trigger">{L.select}</Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-2 max-h-72 overflow-y-auto">
                {roles.map((r) => (
                  <label key={r.key} className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-stone-50 cursor-pointer">
                    <Checkbox checked={manual.global_roles.includes(r.key)} onCheckedChange={() => toggleManual('global_roles', r.key)}
                      data-testid={`manual-role-${r.key}`} />
                    <span className="text-sm text-stone-700">{r.label}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
            <Chips values={manual.global_roles} labelFn={roleLabel} />
          </div>
          <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600">
            <UserCircle size={13} /> {L.users}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs border-stone-300" data-testid="manual-users-trigger">{L.select}</Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-2 max-h-72 overflow-y-auto">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-1.5 py-1.5 rounded-sm hover:bg-stone-50 cursor-pointer min-w-0">
                    <Checkbox checked={manual.user_ids.includes(u.id)} onCheckedChange={() => toggleManual('user_ids', u.id)} />
                    <span className="text-sm text-stone-700 truncate">{u.name} <span className="text-stone-400 text-xs">{u.email}</span></span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
            <Chips values={manual.user_ids} labelFn={userLabel} />
          </div>
          <Button onClick={sendManual} disabled={sending} data-testid="manual-send-btn"
            className="ml-auto bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1.5 h-8">
            <PaperPlaneTilt size={14} /> {sending ? '...' : L.send}
          </Button>
        </div>
      </div>

      {events.map((ev, idx) => (
        <div key={ev.event_type} data-testid={`notif-rule-${ev.event_type}`}
          className="rounded-sm border border-stone-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={!!ev.enabled} onCheckedChange={(v) => patch(idx, 'enabled', v)}
              data-testid={`notif-${ev.event_type}-enabled`} />
            <span className="font-medium text-stone-900 flex-1">{es ? ev.label_es : ev.label_en}</span>
            {ev.scope === 'study' && (
              <Badge variant="outline" className="border-stone-300 text-stone-500 text-[11px]">{L.studyScope}</Badge>
            )}
            <Button size="sm" onClick={() => save(ev)} disabled={savingId === ev.event_type}
              data-testid={`notif-${ev.event_type}-save`}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1.5 h-8">
              <FloppyDisk size={14} /> {savingId === ev.event_type ? '...' : L.save}
            </Button>
          </div>

          <div className={`grid gap-3 pl-11 ${ev.scope === 'study' ? 'md:grid-cols-3' : 'md:grid-cols-2'} ${ev.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600">
                <Users size={13} /> {L.globalRoles} <MultiRoles idx={idx} field="global_roles" selected={ev.global_roles} />
              </div>
              <Chips values={ev.global_roles} labelFn={roleLabel} />
            </div>
            {ev.scope === 'study' && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600">
                  <Buildings size={13} /> {L.studyRoles} <MultiRoles idx={idx} field="study_roles" selected={ev.study_roles} />
                </div>
                <Chips values={ev.study_roles} labelFn={roleLabel} />
              </div>
            )}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-stone-600">
                <UserCircle size={13} /> {L.users} <MultiUsers idx={idx} selected={ev.user_ids} />
              </div>
              <Chips values={ev.user_ids} labelFn={userLabel} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
